import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const TakedownsTab = ({ takedowns, updateTakedownStatus }) => {
  const [notes, setNotes] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [postCache, setPostCache] = useState({}); // record_id → post data

  useEffect(() => {
    // Fetch post data for all unique pending takedown record_ids
    const pendingIds = [...new Set(
      takedowns.filter(t => t.status === 'pending' && t.record_id).map(t => t.record_id)
    )];
    pendingIds.forEach(id => {
      if (postCache[id]) return;
      api.get(`/posts/${id}`).then(r => {
        setPostCache(prev => ({ ...prev, [id]: r.data }));
      }).catch(() => {
        setPostCache(prev => ({ ...prev, [id]: null }));
      });
    });
  }, [takedowns]); // eslint-disable-line

  const handleStatusUpdate = async (takedownId, status) => {
    setProcessingId(takedownId);
    await updateTakedownStatus(takedownId, status, notes[takedownId] || '');
    setProcessingId(null);
    setNotes(prev => ({ ...prev, [takedownId]: '' }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Takedown Requests</h2>
        <div className="text-gray-400 font-mono text-sm">
          {takedowns.filter(t => t.status === 'pending').length} pending
        </div>
      </div>

      {takedowns.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 p-8 rounded-sm text-center">
          <p className="text-gray-500 font-mono">No takedown requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {takedowns.map((takedown) => (
            <div 
              key={takedown.id} 
              className={`bg-gray-900 border p-6 rounded-sm ${
                takedown.status === 'pending' 
                  ? 'border-red-800 bg-red-900/10' 
                  : 'border-gray-800'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-1 text-xs font-mono rounded uppercase ${
                      takedown.status === 'pending' 
                        ? 'bg-red-900/50 text-red-400 border border-red-800' 
                        : takedown.status === 'approved'
                          ? 'bg-green-900/50 text-green-400 border border-green-800'
                          : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}>
                      {takedown.status}
                    </span>
                    <span className="text-gray-500 text-xs font-mono">
                      {new Date(takedown.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-white font-mono text-sm mb-1">
                    Record ID: <span className="text-yellow-400">{takedown.record_id}</span>
                    {takedown.record_id && (
                      <a
                        href={`/post/${takedown.record_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-400 hover:text-blue-300 text-xs underline"
                      >
                        View Post ↗
                      </a>
                    )}
                  </p>
                  {/* Post content preview */}
                  {takedown.record_id && postCache[takedown.record_id] && (
                    <div className="mt-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded p-3">
                      <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-wider mb-1">Content Being Requested for Takedown:</p>
                      {postCache[takedown.record_id].title && (
                        <p className="text-white font-bold text-sm mb-1">{postCache[takedown.record_id].title}</p>
                      )}
                      {postCache[takedown.record_id].content && (
                        <p className="text-gray-300 text-sm leading-relaxed line-clamp-4">
                          {postCache[takedown.record_id].content}
                        </p>
                      )}
                      {postCache[takedown.record_id].user_name && (
                        <p className="text-gray-600 text-xs font-mono mt-1">
                          Posted by: {postCache[takedown.record_id].user_name} · {postCache[takedown.record_id].ward || ''}
                        </p>
                      )}
                    </div>
                  )}
                  {takedown.record_id && postCache[takedown.record_id] === null && (
                    <p className="text-gray-600 text-xs font-mono mt-1">Post may have already been removed.</p>
                  )}
                  {takedown.contact_email && (
                    <p className="text-gray-500 text-xs font-mono">
                      Contact: <a href={`mailto:${takedown.contact_email}`} className="text-blue-400 hover:underline">{takedown.contact_email}</a>
                    </p>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-950 p-4 rounded mb-4">
                <p className="text-gray-400 text-sm font-mono uppercase mb-2">Reason:</p>
                <p className="text-white">{takedown.reason}</p>
              </div>

              {/* Show resolution info if resolved */}
              {takedown.status !== 'pending' && (
                <div className={`p-4 rounded mb-4 ${
                  takedown.status === 'approved' 
                    ? 'bg-green-900/20 border border-green-800' 
                    : 'bg-red-900/20 border border-red-800'
                }`}>
                  <p className="text-gray-400 text-sm font-mono uppercase mb-2">Resolution:</p>
                  <p className={`font-medium ${takedown.status === 'approved' ? 'text-green-400' : 'text-red-400'}`}>
                    {takedown.status === 'approved' ? '✓ Content Removed' : '✕ Request Rejected'}
                  </p>
                  {takedown.admin_notes && (
                    <p className="text-gray-300 text-sm mt-2">Notes: {takedown.admin_notes}</p>
                  )}
                  {takedown.resolved_at && (
                    <p className="text-gray-500 text-xs font-mono mt-2">
                      Resolved: {new Date(takedown.resolved_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              
              {takedown.status === 'pending' && (
                <div className="space-y-3">
                  {/* Admin Notes */}
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1 uppercase">
                      Admin Notes (optional)
                    </label>
                    <textarea
                      value={notes[takedown.id] || ''}
                      onChange={(e) => setNotes(prev => ({ ...prev, [takedown.id]: e.target.value }))}
                      placeholder="Add notes about your decision..."
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-950 border border-gray-700 text-white text-sm rounded-sm resize-none"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStatusUpdate(takedown.id, 'approved')}
                      disabled={processingId === takedown.id}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                    >
                      {processingId === takedown.id ? 'Processing...' : 'Approve & Remove Content'}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(takedown.id, 'rejected')}
                      disabled={processingId === takedown.id}
                      className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TakedownsTab;
